// Brand Color Integration for Basic Themes
// This shows how to use brand colors in the basic theme system

import {
  BrandTheme,
  FlatThemeConfig,
  ThemeColorPalette,
  ThemeConfig,
} from '@dynamic-flow/types';

export const colorPalettes = {
  bh: {
    primary: {
      main: '#8A6E4B',
      light: '#8A6E4B',
      dark: '#8A6E4B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#B9A893',
      light: '#B9A893',
      dark: '#B9A893',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#E5E3E3',
      paper: '#FFFFFF',
      accent: '#F3F4F6',
    },
    text: {
      primary: '#505759',
      secondary: '#919D9D',
      disabled: '#BEC6C4',
    },
    border: {
      light: '#BEC6C4',
      main: '#ABA8A3',
      dark: '#505759',
    },
  },

  vtl: {
    primary: {
      main: '#B89C28',
      light: '#B89C28',
      dark: '#B89C28',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D4C47E',
      light: '#D4C47E',
      dark: '#D4C47E',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#E5E3E3',
      paper: '#FFFFFF',
      accent: '#F3F4F6',
    },
    text: {
      primary: '#6B7280',
      secondary: '#6B7280',
      disabled: '#9CA3AF',
    },
    border: {
      light: '#E5E7EB',
      main: '#6B7280',
      dark: '#374151',
    },
  },
} satisfies Record<string, ThemeColorPalette>;

// Pre-defined company themes
export const brandBasicThemes: Record<BrandTheme, ThemeConfig> = {
  bh: {
    id: 'bh',
    name: 'Bumrungrad',
    description: '',
    colors: colorPalettes.bh,
    typography: {
      fontFamily:
        'open sans, Prompt, Noto Sans JP, Noto Sans SC, Amiri, Padauk, Hanuman,sans-serif',
      fontSizes: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
      },
      fontWeights: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      '2xl': '3rem',
    },
    borderRadius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
    },
    shadows: {
      sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    },
    advanced: {
      id: 'default-advanced',
      name: 'Bumrungrad Advanced',
      description: 'Advanced theme for Bumrungrad',
      colors: {
        primary: {
          name: 'primary',
          purpose: 'Main brand color',
          baseColor: {
            name: 'primary-100',
            percentage: 0,
            hex: '#8B6E4A',
            rgba: [139, 110, 74, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'primary-120',
              percentage: -20,
              hex: '#935003',
              rgba: [147, 80, 3, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'primary-100',
              percentage: 0,
              hex: '#8B6E4A',
              rgba: [139, 110, 74, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'primary-60',
              percentage: 40,
              hex: '#BD9269',
              rgba: [189, 146, 105, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'primary-20',
              percentage: 80,
              hex: '#F5F3F0',
              rgba: [245, 243, 240, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for primary buttons and links',
        },
        secondary: {
          name: 'secondary',
          purpose: 'Secondary brand color for accents and highlights',
          baseColor: {
            name: 'secondary-100',
            percentage: 0,
            hex: '#008270',
            rgba: [0, 130, 112, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'secondary-120',
              percentage: -20,
              hex: '#006B5A',
              rgba: [0, 107, 90, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'secondary-100',
              percentage: 0,
              hex: '#008270',
              rgba: [0, 130, 112, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'secondary-60',
              percentage: 40,
              hex: '#4DA69A',
              rgba: [77, 166, 154, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'secondary-20',
              percentage: 80,
              hex: '#E6F5F3',
              rgba: [230, 245, 243, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for secondary buttons and accents',
        },
        warning: {
          name: 'warning',
          purpose: 'Warning and caution states',
          baseColor: {
            name: 'warning-100',
            percentage: 0,
            hex: '#F59E0B',
            rgba: [245, 158, 11, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'warning-120',
              percentage: -20,
              hex: '#D97706',
              rgba: [217, 119, 6, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'warning-100',
              percentage: 0,
              hex: '#F59E0B',
              rgba: [245, 158, 11, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'warning-60',
              percentage: 40,
              hex: '#FCD34D',
              rgba: [252, 211, 77, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'warning-20',
              percentage: 80,
              hex: '#FEF3C7',
              rgba: [254, 243, 199, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for warning buttons and alerts',
        },
        negative: {
          name: 'negative',
          purpose: 'Error and danger states',
          baseColor: {
            name: 'negative-100',
            percentage: 0,
            hex: '#EF4444',
            rgba: [239, 68, 68, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'negative-120',
              percentage: -20,
              hex: '#DC2626',
              rgba: [220, 38, 38, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'negative-100',
              percentage: 0,
              hex: '#EF4444',
              rgba: [239, 68, 68, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'negative-60',
              percentage: 40,
              hex: '#FCA5A5',
              rgba: [252, 165, 165, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'negative-20',
              percentage: 80,
              hex: '#FEE2E2',
              rgba: [254, 226, 226, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for error buttons and alerts',
        },
        information: {
          name: 'information',
          purpose: 'Info and neutral states',
          baseColor: {
            name: 'information-100',
            percentage: 0,
            hex: '#3B82F6',
            rgba: [59, 130, 246, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'information-120',
              percentage: -20,
              hex: '#1D4ED8',
              rgba: [29, 78, 216, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'information-100',
              percentage: 0,
              hex: '#3B82F6',
              rgba: [59, 130, 246, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'information-60',
              percentage: 40,
              hex: '#93C5FD',
              rgba: [147, 197, 253, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'information-20',
              percentage: 80,
              hex: '#EFF6FF',
              rgba: [239, 246, 255, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for info buttons and links',
        },
        success: {
          name: 'success',
          purpose: 'Success and positive states',
          baseColor: {
            name: 'success-100',
            percentage: 0,
            hex: '#10B981',
            rgba: [16, 185, 129, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'success-120',
              percentage: -20,
              hex: '#059669',
              rgba: [5, 150, 105, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'success-100',
              percentage: 0,
              hex: '#10B981',
              rgba: [16, 185, 129, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'success-60',
              percentage: 40,
              hex: '#6EE7B7',
              rgba: [110, 231, 183, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'success-20',
              percentage: 80,
              hex: '#D1FAE5',
              rgba: [209, 250, 229, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for success buttons and alerts',
        },
        neutral: {
          name: 'neutral',
          purpose: 'Background, text, and border colors',
          baseColor: {
            name: 'neutral-100',
            percentage: 0,
            hex: '#6B7280',
            rgba: [107, 114, 128, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'neutral-120',
              percentage: -20,
              hex: '#4B5563',
              rgba: [75, 85, 99, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'neutral-100',
              percentage: 0,
              hex: '#6B7280',
              rgba: [107, 114, 128, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'neutral-60',
              percentage: 40,
              hex: '#9CA3AF',
              rgba: [156, 163, 175, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'neutral-20',
              percentage: 80,
              hex: '#F3F4F6',
              rgba: [243, 244, 246, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes:
            'Neutral colors should maintain sufficient contrast for text readability',
        },
      },
      typography: {
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      accessibility: {
        aaCompliance: true,
        aaaCompliance: false,
        minContrastRatio: 4.5,
      },
    },
  },

  vtl: {
    id: 'vtl',
    name: 'Vitalife',
    description: '',
    colors: colorPalettes.vtl,
    typography: {
      fontFamily: 'Georgia, serif',
      fontSizes: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
      },
      fontWeights: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      '2xl': '3rem',
    },
    borderRadius: {
      sm: '0.125rem',
      md: '0.25rem',
      lg: '0.375rem',
      xl: '0.5rem',
    },
    shadows: {
      sm: '0 1px 2px 0 rgb(138 110 75 / 0.1)',
      md: '0 4px 6px -1px rgb(138 110 75 / 0.15), 0 2px 4px -2px rgb(138 110 75 / 0.1)',
      lg: '0 10px 15px -3px rgb(138 110 75 / 0.2), 0 4px 6px -4px rgb(138 110 75 / 0.1)',
    },
    advanced: {
      id: 'vtl-advanced',
      name: 'Vitalife Advanced',
      description: 'Advanced theme for Vitalife',
      colors: {
        primary: {
          name: 'primary',
          purpose: 'Main brand color',
          baseColor: {
            name: 'primary-100',
            percentage: 0,
            hex: '#B89C28',
            rgba: [184, 156, 40, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'primary-120',
              percentage: -20,
              hex: '#9A7F1F',
              rgba: [154, 127, 31, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'primary-100',
              percentage: 0,
              hex: '#B89C28',
              rgba: [184, 156, 40, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'primary-60',
              percentage: 40,
              hex: '#D4C85A',
              rgba: [212, 200, 90, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'primary-20',
              percentage: 80,
              hex: '#F7F4E8',
              rgba: [247, 244, 232, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for primary buttons and links',
        },
        secondary: {
          name: 'secondary',
          purpose: 'Secondary brand color for accents and highlights',
          baseColor: {
            name: 'secondary-100',
            percentage: 0,
            hex: '#6B7280',
            rgba: [107, 114, 128, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'secondary-120',
              percentage: -20,
              hex: '#4B5563',
              rgba: [75, 85, 99, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'secondary-100',
              percentage: 0,
              hex: '#6B7280',
              rgba: [107, 114, 128, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'secondary-60',
              percentage: 40,
              hex: '#9CA3AF',
              rgba: [156, 163, 175, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'secondary-20',
              percentage: 80,
              hex: '#F3F4F6',
              rgba: [243, 244, 246, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for secondary buttons and accents',
        },
        warning: {
          name: 'warning',
          purpose: 'Warning and caution states',
          baseColor: {
            name: 'warning-100',
            percentage: 0,
            hex: '#F59E0B',
            rgba: [245, 158, 11, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'warning-120',
              percentage: -20,
              hex: '#D97706',
              rgba: [217, 119, 6, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'warning-100',
              percentage: 0,
              hex: '#F59E0B',
              rgba: [245, 158, 11, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'warning-60',
              percentage: 40,
              hex: '#FCD34D',
              rgba: [252, 211, 77, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'warning-20',
              percentage: 80,
              hex: '#FEF3C7',
              rgba: [254, 243, 199, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for warning buttons and alerts',
        },
        negative: {
          name: 'negative',
          purpose: 'Error and danger states',
          baseColor: {
            name: 'negative-100',
            percentage: 0,
            hex: '#EF4444',
            rgba: [239, 68, 68, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'negative-120',
              percentage: -20,
              hex: '#DC2626',
              rgba: [220, 38, 38, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'negative-100',
              percentage: 0,
              hex: '#EF4444',
              rgba: [239, 68, 68, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'negative-60',
              percentage: 40,
              hex: '#FCA5A5',
              rgba: [252, 165, 165, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'negative-20',
              percentage: 80,
              hex: '#FEE2E2',
              rgba: [254, 226, 226, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for error buttons and alerts',
        },
        information: {
          name: 'information',
          purpose: 'Info and neutral states',
          baseColor: {
            name: 'information-100',
            percentage: 0,
            hex: '#3B82F6',
            rgba: [59, 130, 246, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'information-120',
              percentage: -20,
              hex: '#1D4ED8',
              rgba: [29, 78, 216, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'information-100',
              percentage: 0,
              hex: '#3B82F6',
              rgba: [59, 130, 246, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'information-60',
              percentage: 40,
              hex: '#93C5FD',
              rgba: [147, 197, 253, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'information-20',
              percentage: 80,
              hex: '#EFF6FF',
              rgba: [239, 246, 255, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for info buttons and links',
        },
        success: {
          name: 'success',
          purpose: 'Success and positive states',
          baseColor: {
            name: 'success-100',
            percentage: 0,
            hex: '#10B981',
            rgba: [16, 185, 129, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'success-120',
              percentage: -20,
              hex: '#059669',
              rgba: [5, 150, 105, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'success-100',
              percentage: 0,
              hex: '#10B981',
              rgba: [16, 185, 129, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'success-60',
              percentage: 40,
              hex: '#6EE7B7',
              rgba: [110, 231, 183, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'success-20',
              percentage: 80,
              hex: '#D1FAE5',
              rgba: [209, 250, 229, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes: 'Use 100 for success buttons and alerts',
        },
        neutral: {
          name: 'neutral',
          purpose: 'Background, text, and border colors',
          baseColor: {
            name: 'neutral-100',
            percentage: 0,
            hex: '#6B7280',
            rgba: [107, 114, 128, 1] as [number, number, number, number],
            contrastRatio: 4.5,
            accessibilityLevel: 'AA' as const,
          },
          variations: {
            120: {
              name: 'neutral-120',
              percentage: -20,
              hex: '#4B5563',
              rgba: [75, 85, 99, 1] as [number, number, number, number],
              contrastRatio: 7.0,
              accessibilityLevel: 'AAA' as const,
            },
            100: {
              name: 'neutral-100',
              percentage: 0,
              hex: '#6B7280',
              rgba: [107, 114, 128, 1] as [number, number, number, number],
              contrastRatio: 4.5,
              accessibilityLevel: 'AA' as const,
            },
            60: {
              name: 'neutral-60',
              percentage: 40,
              hex: '#9CA3AF',
              rgba: [156, 163, 175, 1] as [number, number, number, number],
              contrastRatio: 3.0,
              accessibilityLevel: 'Fail' as const,
            },
            20: {
              name: 'neutral-20',
              percentage: 80,
              hex: '#F3F4F6',
              rgba: [243, 244, 246, 1] as [number, number, number, number],
              contrastRatio: 1.2,
              accessibilityLevel: 'Fail' as const,
            },
          },
          mostUsed: '100' as const,
          accessibilityNotes:
            'Neutral colors should maintain sufficient contrast for text readability',
        },
      },
      typography: {
        fontFamily: 'Georgia, serif',
      },
      accessibility: {
        aaCompliance: true,
        aaaCompliance: false,
        minContrastRatio: 4.5,
      },
    },
  },
};

// Example usage in your FlowBuilder:
/*
// In FlowBuilder.tsx, you can now use brand colors in basic themes:
const handleBrandBasicThemeChange = (brandName: 'bumrungrad' | 'vitalife') => {
  const brandTheme = brandBasicThemes[brandName];
  
  const updatedFlow = {
    ...currentFlow,
    theme: brandTheme, // Direct assignment to basic theme
  };
  
  setCurrentFlow(updatedFlow);
};
*/

// CSS Variable generation for brand basic themes
export function generateBrandBasicCSSVariables(theme: FlatThemeConfig): string {
  console.log('generate ', theme.colors);
  return `
    :root {
      /* Brand-specific basic theme colors */
      --color-primary: ${theme.colors.primary};
      --color-secondary: ${theme.colors.secondary};
      --color-background: ${theme.colors.background};
      --color-warning: ${theme.colors.warning};
      --color-error: ${theme.colors.error};
      --color-success: ${theme.colors.success};
      --color-text: ${theme.colors.text};
      --color-border: ${theme.colors.border};
      
      /* Typography */
      --font-family: ${theme.typography.fontFamily};
      
      /* Spacing */
      --spacing-xs: ${theme.spacing.xs};
      --spacing-sm: ${theme.spacing.sm};
      --spacing-md: ${theme.spacing.md};
      --spacing-lg: ${theme.spacing.lg};
      --spacing-xl: ${theme.spacing.xl};
      --spacing-2xl: ${theme.spacing['2xl']};
      
      /* Border radius */
      --border-radius-sm: ${theme.borderRadius.sm};
      --border-radius-md: ${theme.borderRadius.md};
      --border-radius-lg: ${theme.borderRadius.lg};
      --border-radius-xl: ${theme.borderRadius.xl};
      
      /* Shadows */
      --shadow-sm: ${theme.shadows.sm};
      --shadow-md: ${theme.shadows.md};
      --shadow-lg: ${theme.shadows.lg};
    }
  `;
}
